import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

export const FLOAT_PANEL_MARGIN = 12;

/**
 * Mantém um painel position:fixed dentro da viewport.
 * @param {{ preferredTop?: number, preferredLeft?: number, width?: number, height?: number, margin?: number }} opts
 */
export function clampFixedPanelCoords({
  preferredTop,
  preferredLeft,
  width = 0,
  height = 0,
  margin = FLOAT_PANEL_MARGIN,
} = {}) {
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 768;
  const h = Math.max(0, Number(height) || 0);
  const w = Math.max(0, Number(width) || 0);
  const maxTop = Math.max(margin, vh - h - margin);
  const maxLeft = Math.max(margin, vw - w - margin);
  const top = Math.max(margin, Math.min(Number(preferredTop) || margin, maxTop));
  const left = Math.max(margin, Math.min(Number(preferredLeft) || margin, maxLeft));
  return { top, left };
}

/**
 * Hook: mede o painel e reposiciona quando conteúdo/viewport mudam.
 * @param {{ top?: number, left?: number } | null | undefined} preferred
 * @param {unknown[]} [deps] — ex.: menu, suggestion, loading
 */
export function useClampedFixedPanel(preferred, deps = []) {
  const ref = useRef(null);
  const [coords, setCoords] = useState(() => ({
    top: preferred?.top ?? FLOAT_PANEL_MARGIN,
    left: preferred?.left ?? FLOAT_PANEL_MARGIN,
  }));

  const clamp = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const next = clampFixedPanelCoords({
      preferredTop: preferred?.top,
      preferredLeft: preferred?.left,
      width: Math.max(el.offsetWidth, el.getBoundingClientRect().width),
      height: Math.max(el.offsetHeight, el.getBoundingClientRect().height),
    });
    setCoords((prev) => (prev.top === next.top && prev.left === next.left ? prev : next));
  }, [preferred?.top, preferred?.left]);

  useLayoutEffect(() => {
    clamp();
    // Re-mede após layout (conteúdo assíncrono / fontes)
    const t = window.setTimeout(clamp, 0);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps extras controlam re-clamp
  }, [clamp, ...deps]);

  useEffect(() => {
    window.addEventListener('resize', clamp);
    return () => window.removeEventListener('resize', clamp);
  }, [clamp]);

  return { ref, coords, clamp };
}
